<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';

import DynamicTabs from '@/components/DynamicTabs.vue';
import ConsultationResults from '@/components/ConsultationResults.vue';
import ConsultationSummary from '@/components/ConsultationSummary.vue';
import ConsultationAIAnalysis from '@/components/ConsultationAIAnalysis.vue';
import PdfExport from '@/components/PdfExport.vue';

import { useCommunityStore, useUsersStore } from '@/stores';
import type { Locales } from '@/services/types/api.types';
import { Constants } from '@/constants';

// CONST
const { t } = useI18n();

// STORE
const communityStore = useCommunityStore();
const userStore = useUsersStore();
const { community } = storeToRefs(communityStore);
const { currentUser } = storeToRefs(userStore);

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  consultationId: number;
}>();

const canAccessAiAnalysis = computed(() => {
  if (!currentUser.value) return false;
  if (currentUser.value.isSuperadmin) return true;

  const aiAnalysisEnabled = community.value?.options.export.ai_analysis ?? false;
  return aiAnalysisEnabled && [Constants.User.Role.ADMIN, Constants.User.Role.EDITOR].includes(currentUser.value.role);
});

const tabs = computed(() => {
  const tabList = [
    {
      id: 'summary',
      name: t('consultation.tabs.summary'),
      icon: 'bar_chart',
      disable: false,
    },
    {
      id: 'results',
      name: t('consultation.tabs.reporting'),
      icon: 'file_download',
      disable: false,
    },
  ];
  if (canAccessAiAnalysis.value) {
    tabList.push({
      id: 'aiAnalysis',
      name: t('consultation.tabs.aiAnalysis'),
      icon: 'model_training',
      disable: false,
    });
  }
  if (canAccessAiAnalysis.value || (currentUser.value != null && currentUser.value.isSuperadmin)) {
    tabList.push({
      id: 'pdfReport',
      name: t('consultation.tabs.pdfReport'),
      icon: 'picture_as_pdf',
      disable: !community.value?.options.export.pdf,
    });
  }
  return tabList;
});
</script>
<template>
  <div class="consultation-analyze-tab" v-if="community">
    <dynamic-tabs :tabs="tabs" active="summary" keep-alive>
      <template #summary>
        <ConsultationSummary :consultation-id="props.consultationId" :languageEnabled="props.languageEnabled" />
      </template>
      <template #results>
        <ConsultationResults :consultation-id="props.consultationId" :languageEnabled="props.languageEnabled" />
      </template>
      <template #aiAnalysis>
        <ConsultationAIAnalysis :consultation-id="props.consultationId" :languageEnabled="props.languageEnabled" />
      </template>
      <template #pdfReport>
        <PdfExport :consultation-id="props.consultationId" :languageEnabled="props.languageEnabled" />
      </template>
    </dynamic-tabs>
  </div>
</template>
